import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import Map from './components/map/Map';
import TimeController from "./components/TimeController";
import Footer from "./components/Footer";
import { useLayoutState } from "./store/LayoutState";
import useEffectSkipFirst from "./hooks/useEffectSkipFirst";
import GoogleMapLoader from "./components/map/GoogleMapLoader";
import { Loading } from "./parts/Loading";
import { useRecoilValue } from "recoil";
import { MESSAGE_WIDGET, messageWidgetVisibleState } from "./store/MessageState";
import { elapsedTimeControllerEnable } from "./store/ElapsedTimeState";
import AdminSettingPanel from "./components/admin_setting/AdminSettingPanel";
import { useTimeout } from "./hooks/useTimeout";
import { disabledContentsWithID } from "./store/DisabledContentsState";
import FlightHistoryPanel from './components/copilot/FlightHistoryPanel';
import NavLog from './components/copilot/NavLog';
import { PreLoadIcon } from './components/admin_setting/PointAreaSettingIconList';
import RcInformationPanel from "./components/map/RcInformationPanel";
import { lazyIdle } from "./utils/FPEUtils";
import { AudioContextResolver } from "./components/AudioContextResolver";
const Header = lazyIdle(() => import("./components/header/Header"));
const MessageWidget = lazyIdle(() => import("./components/message/MessageWidget"));
const MessageDataObserver = lazyIdle(() => import("./components/message/MessageDataObserver"));
const CustomerConfigObserver = lazyIdle(() => import("./CustomerConfigObserver"));
const UserConfigObserver= lazyIdle(() => import("./UserConfigObserver"));
const FlightPlanPanel = lazyIdle(() => import("./components/flightplan/FlightPlanPanel"));
const TCInformationPanel = lazyIdle(() => import("./components/map/TCInformationPanel"));
const FPECrossSection = lazyIdle(() => import("./components/crosssection/FPECrossSection"));
const LegendBox = lazyIdle(() => import("./components/LegendBox"), -1);

function App() {

  const {layout, isHorizontal, isVertical} = useLayoutState();
  const messageWidgetVisible = useRecoilValue(messageWidgetVisibleState);
  const [rightPaneVisible, setRightPaneVisible] = useState(messageWidgetVisible);
  const timeControllerEnable = useRecoilValue(elapsedTimeControllerEnable);
  const leftPaneRef = useRef(null);
  const rightPaneRef = useRef(null);
  const paneResizerRef = useRef(null);
  const pageWrapRef = useRef(null);
  const mapWrapRef = useRef(null);
  const resizerRef = useRef(null);
  const rightPaneFloatingRef = useRef(false);
  const messageDisabled = useRecoilValue(disabledContentsWithID(MESSAGE_WIDGET));

  useEffectSkipFirst(() => {
    mapWrapRef.current.style.width = null;
    mapWrapRef.current.style.height = null;
  }, layout);

  const handleResizerMouseDown = useCallback(() => {
    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', onMouseUp);
    const pageHeight = pageWrapRef.current.clientHeight;
    mapWrapRef.current.style.userSelect = 'none';
    document.body.style.cursor = isHorizontal ? 'ns-resize' : 'ew-resize';
    function resize(e) {
      if (isHorizontal) {
        let h = mapWrapRef.current.clientHeight + e.movementY;
        if (h > pageHeight) {
          h = pageHeight;
        }
        mapWrapRef.current.style.height = h + 'px';
      } else if (isVertical) {
        let w = mapWrapRef.current.clientWidth + e.movementX;
        mapWrapRef.current.style.width = `${w}px`;
      }
    }
    function onMouseUp() {
      mapWrapRef.current.style.userSelect = 'auto';
      document.body.style.cursor = '';
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousemove', resize);
    }
  }, [isHorizontal, isVertical]);

  const handlePaneResizerMouseDown = useCallback(() => {
    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', onMouseUp);

    leftPaneRef.current.style.userSelect = 'none';
    rightPaneRef.current.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
    paneResizerRef.current.classList.add('resizer-moving');
    function resize(e) {
      let w = rightPaneRef.current.clientWidth - e.movementX;
      if (w <= 295) {
        //現在のMessageWidgetのminWidthの設定上、295以下はスクロールバーが出現する
        return;
      }
      leftPaneRef.current.style.width = `calc(100% - ${w}px)`;
    }
    function onMouseUp() {
      leftPaneRef.current.style.userSelect = 'auto';
      rightPaneRef.current.style.userSelect = 'auto';
      document.body.style.cursor = '';
      paneResizerRef.current.classList.remove('resizer-moving');
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousemove', resize);
    }
  }, []);

  const [changeRightPaneVisible] = useTimeout(() => {
    setRightPaneVisible(messageWidgetVisible);
  },1);

  useEffect(() => {
    changeRightPaneVisible()
  }, [messageWidgetVisible, changeRightPaneVisible]);

  useEffect(() => {
    if (rightPaneVisible && !rightPaneFloatingRef.current) {
      leftPaneRef.current.style.flexShrink = 1
    } else {
      leftPaneRef.current.style.flexShrink = 0
      leftPaneRef.current.style.width = `100%`;
    }
    setRightPaneVisible(rightPaneVisible);
  }, [rightPaneVisible]);

  const handleMessageLayoutChange = useCallback((floating) => {
    if (floating) {
      leftPaneRef.current.style.flexShrink = 0
      leftPaneRef.current.style.width = `100%`;
    } else {
      leftPaneRef.current.style.flexShrink = 1
    }
    rightPaneFloatingRef.current = floating;
    paneResizerRef.current.style.display = floating ? 'none' : 'block';
  },[]);

  return (
    <div id="app" className={`App ${layout}`}>
      <div className="main-layout">
        <div className="left-pane" ref={leftPaneRef}>
          <div id="page-wrap" className="page-wrap" ref={pageWrapRef}>
            <div id="page" className="page">
              <div id="map-wrap" className="map-wrap" ref={mapWrapRef}>
                <Suspense fallback={""}>
                  <Header/>
                </Suspense>
                <Suspense fallback={<Loading/>}>
                  <GoogleMapLoader>
                    <Map/>
                  </GoogleMapLoader>
                </Suspense>
              </div>
              <div className="resizer">
                <div className="page-resizer" ref={resizerRef} onMouseDown={handleResizerMouseDown}/>
              </div>
              <div className="cross-wrap">
                <Suspense fallback={""}>
                  <FPECrossSection/>
                </Suspense>
              </div>
              <Suspense fallback={""}>
                <FlightPlanPanel/>
              </Suspense>
              <Suspense fallback={<Loading/>}>
                <FlightHistoryPanel/>
              </Suspense>
              <Suspense fallback={""}>
                <CustomerConfigObserver/>
              </Suspense>
              <AdminSettingPanel/>
              <PreLoadIcon />
              <Suspense fallback={""}>
                <NavLog/>
              </Suspense>
              <Suspense fallback={""}>
                <TCInformationPanel/>
              </Suspense>
              <RcInformationPanel/>
            </div>
          </div>
          {
            timeControllerEnable &&
            <div className="time-controller-wrap">
              <TimeController/>
            </div>
          }
          <Suspense fallback={""}>
            <LegendBox/>
          </Suspense>
        </div>
        <div className="resizer-pane" hidden={!rightPaneVisible}>
          <div className="pane-resizer" onMouseDown={handlePaneResizerMouseDown} ref={paneResizerRef}/>
        </div>
        <div className="right-pane" hidden={!rightPaneVisible} ref={rightPaneRef}>
          <Suspense fallback={""}>
            <MessageWidget onLayoutChange={handleMessageLayoutChange}/>
          </Suspense>
        </div>
      </div>
      <Footer/>
      <Suspense fallback={""}>
        <UserConfigObserver/>
      </Suspense>
      <Suspense fallback={""}>
        <AudioContextResolver>
          {
            !messageDisabled &&
            <Suspense fallback={""}>
              <MessageDataObserver/>
            </Suspense>
          }
        </AudioContextResolver>
      </Suspense>
    </div>
  );
}

export default App;
